import React, { useEffect, useState, ReactNode, useCallback, useMemo } from 'react';
import { ClerkProvider, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '@/contexts/auth-context';
import { parseIframeParams, type IframeParams } from '@/lib/iframe-params';
import {
  setAuthTokenGetter,
  setUnauthenticatedHandler,
  redirectToSignIn,
  resetSignInRedirect,
} from '@/lib/auth-token';
import { exchangeClerkSession } from '@/lib/clerk-session';
import {
  storeAuthData,
  getStoredSsoToken,
  getStoredToken,
  getStoredUser,
  clearAuthData,
} from '@/utils/auth';
import { queryKeys } from '@/api/react-query.config';
import { getTenantBasePath } from '@low6-pickem/frontend-shared';
import { getClerkLocalization } from './clerkLocalization';

const TENANT_BASE_PATH = getTenantBasePath();

type ClerkSessionUser = {
  sso_user_id: string;
  username: string;
  email?: string;
};

interface ClerkTenantAuthProviderProps {
  children: ReactNode;
  publishableKey: string;
}

function ClerkAuthProviderInner({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken, signOut } = useClerkAuth();
  const queryClient = useQueryClient();
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [user, setUser] = useState<{
    id: string;
    sso_user_id: string;
    username: string;
  } | null>(null);
  const [iframeParams] = useState<IframeParams>(() => parseIframeParams());

  const applyInternalSession = useCallback(
    (token: string, userData: ClerkSessionUser) => {
      const normalizedUser = {
        id: userData.sso_user_id,
        sso_user_id: userData.sso_user_id,
        username: userData.username,
      };

      storeAuthData({ token, user: normalizedUser });
      setAuthToken(token);
      setUser(normalizedUser);
      setIsAuthenticated(true);
      setAuthError(null);
      resetSignInRedirect();
      queryClient.invalidateQueries({ queryKey: queryKeys.user.all });
    },
    [queryClient]
  );

  useEffect(() => {
    setAuthTokenGetter(async () => getStoredSsoToken() || getStoredToken());
    setUnauthenticatedHandler(() => {
      redirectToSignIn();
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const syncSession = async () => {
      if (!isLoaded) {
        return;
      }

      if (!isSignedIn) {
        clearAuthData();
        if (!cancelled) {
          setAuthToken(null);
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
        }
        return;
      }

      const storedToken = getStoredSsoToken() || getStoredToken();
      const storedUser = getStoredUser();

      if (storedToken && storedUser) {
        if (!cancelled) {
          setAuthToken(storedToken);
          setUser(storedUser);
          setIsAuthenticated(true);
          setIsLoading(false);
        }
        return;
      }

      try {
        const clerkToken = await getToken();
        if (!clerkToken) {
          throw new Error('Clerk session token is unavailable');
        }

        const result = await exchangeClerkSession(clerkToken);
        if (!cancelled) {
          applyInternalSession(result.token, result.user);
          setIsLoading(false);
        }
      } catch (error) {
        if (!cancelled) {
          const nextError =
            error instanceof Error ? error : new Error('Clerk session exchange failed');
          setAuthError(nextError);
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      }
    };

    setIsLoading(true);
    syncSession();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken, applyInternalSession]);

  const logout = async () => {
    clearAuthData();
    setAuthToken(null);
    setUser(null);
    setIsAuthenticated(false);
    await signOut();
  };

  const loginWithOAuthCode = async (
    token: string,
    userData: { sso_user_id?: string; id?: string; username: string }
  ) => {
    applyInternalSession(token, {
      sso_user_id: userData.sso_user_id || userData.id || '',
      username: userData.username,
    });
    setIsLoading(false);
  };

  return (
    <AuthContext.Provider
      value={{
        authToken,
        isAuthenticated,
        isLoading,
        authError,
        loginWithOAuthCode,
        logout,
        user,
        iframeParams,
        ssoUserId: user?.sso_user_id ?? null,
        username: user?.username ?? null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const ClerkTenantAuthProvider: React.FC<ClerkTenantAuthProviderProps> = ({
  children,
  publishableKey,
}) => {
  const { t, i18n } = useTranslation();
  const localization = useMemo(() => getClerkLocalization(i18n.language, t), [i18n.language, t]);

  return (
    <ClerkProvider
      key={i18n.language}
      publishableKey={publishableKey}
      localization={localization}
      signInUrl={`${TENANT_BASE_PATH}/sign-in`}
      signUpUrl={`${TENANT_BASE_PATH}/sign-up`}
      signInFallbackRedirectUrl={TENANT_BASE_PATH || '/'}
      signUpFallbackRedirectUrl={TENANT_BASE_PATH || '/'}
      afterSignOutUrl={`${TENANT_BASE_PATH}/`}
    >
      <ClerkAuthProviderInner>{children}</ClerkAuthProviderInner>
    </ClerkProvider>
  );
};
